// src/pages/PrivacySettings.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import '../styles/PrivacySettings.css';
import { useNavigate } from 'react-router-dom';

const PrivacySettings = () => {
  const [settings, setSettings] = useState({
    shareLocationOnContact: true,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Enable scrolling for this page and fetch settings
  useEffect(() => {
    document.body.classList.add('privacy-settings-page-active');
    document.documentElement.classList.add('privacy-settings-page-active');
    const fetchSettings = async () => {
      try {
        const response = await api.get('/api/user/privacy-settings');
        const data = response.data;
        setSettings({
          shareLocationOnContact: data.shareLocationOnContact !== undefined ? data.shareLocationOnContact : true,
        });
        setError('');
      } catch (err) {
        console.error('Failed to fetch privacy settings', err);
        setError(err.response?.data?.error || 'Failed to load privacy settings');
        setSettings({ shareLocationOnContact: true });
      }
    };
    fetchSettings();
    return () => {
      document.body.classList.remove('privacy-settings-page-active');
      document.documentElement.classList.remove('privacy-settings-page-active');
    };
  }, []);

  // Handler to update privacy settings
  const handleSettingChange = async (settingName, value) => {
    // Save previous state in case we need to revert
    const previousSettings = { ...settings };
    const newSettings = { ...settings, [settingName]: value };
    
    // Optimistically update UI
    setSettings(newSettings);
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const response = await api.post('/api/user/privacy-settings', newSettings);

      if (response.status >= 200 && response.status < 300) {
        setSuccessMessage('Privacy settings updated successfully!');
        setError('');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Failed to update privacy settings');
      }
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      console.error('Error details:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to update privacy settings');
      setSuccessMessage('');
      // Revert the change on error
      setSettings(previousSettings);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackClick = () => {
    setIsNavigating(true);
    navigate('/account');
  };

  return (
    <div className="privacy-settings-page">
      <div className="privacy-settings-content">
        <h2>Privacy Settings</h2>
        <p style={{ textAlign: 'center', color: 'var(--premium-text-secondary)', marginBottom: '30px', fontSize: '14px' }}>
          Control how your location information is shared when connecting with others.
        </p>
        {error && <div className="privacy-error">{error}</div>}
        {successMessage && <div className="privacy-success">{successMessage}</div>}
        
        <div className="privacy-setting">
          <label htmlFor="share-location">
            <input
              type="checkbox"
              id="share-location"
              checked={settings.shareLocationOnContact}
              onChange={(e) => handleSettingChange('shareLocationOnContact', e.target.checked)}
              disabled={isSaving}
              style={{ cursor: isSaving ? 'not-allowed' : 'pointer' }}
            />
            Save location when new connections are made 
          </label>
          <p className="setting-description">
            If disabled, location will not be saved for either user at the time of connecting.
          </p>
        </div>
      </div>

      {!isNavigating && (
        <button className="privacy-to-settings-button" onClick={handleBackClick}>
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>Account</span>
        </button>
      )}
    </div>
  );
};

export default PrivacySettings;

